import { useState, type ReactNode } from 'react';
import { Icon } from '../../components/Icon.js';

/** Small upper-case heading over a group of settings. */
export function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: 'var(--text-3)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      {children}
    </div>
  );
}

/** A labelled on/off switch with a one-line hint. */
export function ToggleRow({
  title,
  hint,
  on,
  onToggle,
  boxed,
}: {
  title: string;
  hint?: ReactNode;
  on: boolean;
  onToggle: () => void;
  boxed?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        ...(boxed
          ? {
              padding: '10px 12px',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-lg)',
            }
          : {}),
      }}
    >
      <div>
        <div style={{ fontSize: 13, fontWeight: 700 }}>{title}</div>
        {hint && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{hint}</div>}
      </div>
      <button
        type="button"
        className={'toggle' + (on ? ' on' : '')}
        role="switch"
        aria-checked={on}
        aria-label={title}
        onClick={onToggle}
        style={{ flex: 'none' }}
      />
    </div>
  );
}

/** Side-by-side choice buttons (one pressed). */
export function SegButtons<T extends string>({
  options,
  value,
  onChange,
}: {
  options: readonly (readonly [T, string])[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {options.map(([v, label]) => {
        const on = v === value;
        return (
          <button
            key={v}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(v)}
            style={{
              flex: 1,
              padding: '8px 6px',
              borderRadius: 'var(--r-md)',
              fontSize: 12.5,
              fontWeight: 700,
              border: `1px solid ${on ? 'var(--accent)' : 'var(--border-strong)'}`,
              background: on ? 'var(--accent-soft)' : 'var(--bg-surface)',
              color: on ? 'var(--accent-text)' : 'var(--text-2)',
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/** A collapsible group ("More options", "Connections") with a one-line summary. */
export function Collapsible({
  title,
  summary,
  icon,
  children,
}: {
  title: string;
  summary: string;
  icon?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <section style={{ borderTop: '1px solid var(--border)', paddingTop: 6 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 0',
          border: 'none',
          background: 'transparent',
          textAlign: 'left',
          color: 'var(--text-1)',
        }}
      >
        <span style={{ transform: `rotate(${open ? 90 : 0}deg)`, transition: 'transform .14s', color: 'var(--text-3)' }}>
          <Icon name="chevronR" size={14} />
        </span>
        <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
          <span style={{ fontWeight: 800, fontSize: 13.5 }}>{title}</span>
          <br />
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{summary}</span>
        </span>
        {icon && (
          <span style={{ color: 'var(--text-3)' }}>
            <Icon name={icon} size={15} />
          </span>
        )}
      </button>
      {open && <div style={{ display: 'grid', gap: 16, padding: '6px 0 4px 22px' }}>{children}</div>}
    </section>
  );
}

/** A compact square icon button (↑, ↓, remove). */
export function MiniButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className="icon-btn"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      style={{ width: 28, height: 28, fontSize: 12, flex: 'none' }}
    >
      {children}
    </button>
  );
}
